/**
 * 作り直しの行契約 — 要素の表 10 枚の列定義（大枠設計 §3.1・§3.2、HIST §3）。
 *
 * **このファイルが `spec_graph_*` 10 枚の列定義の導出元である。** BigQuery のスキーマは契約から
 * `zodToBqSchema` で導出されるので、`infra/graph/spec/bigquery.ts` がここの行スキーマを
 * `SPEC_ELEMENT_TABLE_IDS`（`table-ids.ts`）の名前に載せて 10 枚を作る。
 *
 * 旧い形の表（id が STRING）は T1a で消し、同じ名前の表をこの行スキーマで空から作り直した。
 * ID が STRING から INT64 へ替わるので、同じ表に載せ替えることはできなかった。
 *
 * 旧い形との差は 3 つ。
 *
 * | 表 | 今 | 新しい形 |
 * |---|---|---|
 * | nodes | 12 列。`id` は STRING（パス） | **9 列**。`id` は INT64。`name` / `qualifiedName` / `path` を落とす |
 * | edges | 10 列。`id` は STRING | **9 列**。`id` の列を持たない |
 *
 * `name` / `qualifiedName` / `path` を落とすのは、ID が number になると入れるものが無くなる
 * ためである（今は `qualifiedName` ＝ パス、`name` ＝ その末尾、`path` は常に NULL）。今の
 * 契約が「恒久的に埋まらない列を作らない」を理由に `startLine` / `endLine` / `isExported` を
 * 持たない判断をしたのと同じ向きである。表を作り直す切替のときが、列を落とせる唯一の機会で
 * ある。
 *
 * エッジが `id` を持たないのは、エッジが宣言の射影で、書込のたびに導出し直して置き換わる
 * ためである。同一性は（型, 起点の id, 終点の id）の組が決める。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 作り直しの spec グラフ行スキーマ（ノード 9 列 / エッジ 9 列 / 案件の変更 7 列 / 操作履歴 9 列 / 要求の層 6 表）。切替で BQ スキーマの導出元になる
 * @graph-connects none
 */
import { bqFloatArray, bqInt, bqString, bqTimestamp } from '@cortex/bigquery/schema';
import { z } from 'zod';
/**
 * 作り直しのノード行（9 列）。
 *
 * `id` が INT64 になるので、**型の指定なしに id だけでノードが一意に決まる**。今のパス形は
 * 型を一意に決めないため、読取に `node_type` を添えて呼び直す経路が要っていた。
 *
 * `textForEmbedding` は、参照をラベルに解決してから組む。新しい形の `properties` は大半が
 * 番号の参照になるので、文字列をつなぐだけでは中身がほぼ空になる。
 *
 * `embedding` は列として残す。ベクトル検索を入れるかは作り直しの範囲に入れない。
 *
 * `id` が正であることは**列の型ではなく行全体の検査で見る**。`bqInt()` が返すスキーマに
 * `.positive()` を継ぎ足すと別のインスタンスになり、BQ の型メタ（`zodToBqSchema` が引く
 * WeakMap の登録）が外れて列の導出が落ちる。行はすでに採番が済んだ最終形なので、
 * `elements/ids.ts` の仮の番号（負の数）がここへ残っていたら受け付けない。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 作り直しの spec_graph_nodes の行スキーマ契約（9 列。id は INT64 の正の整数）
 * @graph-connects none
 */
export const specElementNodeRowSchema = z
    .object({
    id: bqInt('ノード一意ID。全型で 1 本の連番'),
    graphId: bqString('グラフ識別子。spec-graph 固定'),
    snapshotId: bqString('書込の世代を表す固定値').nullable(),
    repository: bqString('仕様の正を持つリポジトリ名（cortex）'),
    nodeType: bqString('ノード種別 — Unit, Verb, Actor, Domain, Stage, Term, Rule, Business, Screen, ' +
        'Component, Flow'),
    properties: bqString('追加プロパティ JSON — 名前の作り・欄の値・参照する id').nullable(),
    textForEmbedding: bqString('ベクトル検索用テキスト — 参照先のラベルに解決してからつないだ本文').nullable(),
    embedding: bqFloatArray('Vertex AI Embedding モデルによるベクトル'),
    updatedAt: bqTimestamp('データ投入日時'),
})
    .refine((row) => row.id > 0, {
    message: '行の id は正の整数である（0 と負の数は採番前・仮の番号のしるし）',
    path: ['id'],
});
/**
 * 作り直しのエッジ行（9 列）。`id` の列を持たない。
 *
 * `sourceId` は必ず仕様グラフのノードなので INT64 である。`targetId` は STRING に据え置く —
 * 仕様グラフの中を指すときは番号だが、外部グラフ（Table / Column / Api / Task）を指すときは
 * その辞書が持つ名前の文字列（`air-closet-styling-api:GET /api/styling/user`）だからである。
 * 1 つの列で両方を持つので、型は広いほうに合わせる。番号用と名前用に列を 2 つ置くと、
 * どちらか一方が必ず NULL の列が 2 本できる（同じ事実の 2 表現 — 制約 11）。
 *
 * `sourceId` が正であることはノード行と同じ理由で行全体の検査で見る。`targetId` は外部グラフ
 * の名前も入る STRING なので、正負の検査を当てる先が無い。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 作り直しの spec_graph_edges の行スキーマ契約（9 列。id 列を持たず、起点は INT64 の正の整数）
 * @graph-connects none
 */
export const specElementEdgeRowSchema = z
    .object({
    graphId: bqString('グラフ識別子。spec-graph 固定'),
    snapshotId: bqString('書込の世代を表す固定値').nullable(),
    edgeType: bqString('エッジ種別 — NE §2 のエッジ語彙'),
    sourceId: bqInt('エッジ起点ノードID。必ず仕様グラフのノード'),
    targetId: bqString('エッジ終点。仕様グラフの中なら id の 10 進表記、外部グラフなら その辞書が持つ名前'),
    sourceRepository: bqString('起点ノードのリポジトリ（cortex）').nullable(),
    targetRepository: bqString('終点ノードのリポジトリ。db-dictionary グラフへの越境エッジは __db_dictionary__ になる').nullable(),
    properties: bqString('追加プロパティ JSON — accessMode 等').nullable(),
    updatedAt: bqTimestamp('データ投入日時'),
})
    .refine((row) => row.sourceId > 0, {
    message: '行の sourceId は正の整数である（0 と負の数は採番前・仮の番号のしるし）',
    path: ['sourceId'],
});
/**
 * 案件の変更の行の状態 5 値（HIST §3.1）。**この列が状態の唯一の置き場である。**
 *
 * 操作履歴（`specOperationRowSchema`）は「誰がいつ何をしたか」だけを持ち、状態の判定に
 * 1 度も使わない。今の実装は確定を `targetKind = 'pj'` の行の有無、承認を
 * `targetKind = 'approval'` の行の有無で表しているが、どちらも行の有無ではなくこの列に移る。
 *
 * | 値 | 意味 | この値になるとき |
 * |---|---|---|
 * | `open` | 未確定。承認の要らない型（業務フロー・業務・処理・画面・部品） | 書込 |
 * | `pending` | 未確定。承認が要る型（`APPROVAL_TARGET_NODE_TYPES`）で、まだ判断されていない。**1 つでも残る案件は確定できない** | 書込。承認済みの行を書き直してもここへ戻る |
 * | `approved` | 担当者が承認した | コンソールの承認 |
 * | `rejected` | 担当者が却下した。1 つでも残る案件は確定できない | コンソールの承認 |
 * | `merged` | 確定済み。1 行でもあればその案件には書けない | 確定 |
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 案件の変更の行が取る状態 5 値の閉集合。未確定 2 値・判断 2 値・確定 1 値
 * @graph-connects none
 */
export const SPEC_PJ_CHANGE_STATUSES = [
    'open',
    'pending',
    'approved',
    'rejected',
    'merged',
];
/**
 * 案件の変更 `spec_graph_pj_changes` の行（7 列。HIST §3.1）。**（案件, ノード）で 1 行**。
 *
 * 今の 11 列（`changeId` / `changedAt` / `actor` / `pjNumber` / `pjSeq` / `targetKind` /
 * `targetId` / `previousId` / `beforeValue` / `afterValue` / `reason`）から 7 列へ落ちる。
 * 落ちる理由は列ごとに違う。
 *
 * | 落ちる列 | 理由 |
 * |---|---|
 * | `changeId` | 行が（案件, ノード）で決まるので自前の id が要らない。書き直しは置き換えであって追記ではない |
 * | `pjSeq` | `pjNumber` が number になり、分割の鍵をそのまま兼ねる |
 * | `targetKind` | 行はノードだけになる。エッジは宣言の射影なので、重ねたあとに導出する |
 * | `previousId` | パスが ID だったころの改名への備え。id が不変の number になると改名は同じ行の更新になる |
 * | `reason` | なぜ変えたかは要求と why の層が持つ（決定 3） |
 *
 * `before` と `after` は**型を含んだノードの値**（`{ nodeType, properties }`）を JSON で持つ。
 * `before` はその案件がそのノードに最後に書いたときの **master の値**で、案件が新しく作った
 * ノードなら null である。`after` は**案件を当てたあとの値**で、案件が消すノードなら null。
 * 両方 null の行は作らない（`after` が master と同じなら行そのものを消す）。
 *
 * **JSON は STRING 列で持つ。** 契約から BQ スキーマを導く `zodToBqSchema` が扱うのは
 * スカラ 5 型（STRING / INT64 / FLOAT64 / BOOL / TIMESTAMP）で、JSON 型を持たない。今の
 * `beforeValue` / `afterValue` も同じ扱いである。中身が JSON オブジェクトであることは
 * 書く側（`core/elements/` の値の符号化）が保証し、読めない値に当たった読み手は投げる —
 * この列を書いたのはこのシステム自身なので、読めない値はデータの不整合だからである。
 *
 * 分割は `pjNumber`、クラスタは `nodeId` と `updatedAt`。案件の変更を読む経路は必ず案件番号の
 * 等値比較（`WHERE pjNumber = @pjNumber`）から入るので、分割がそのまま刈れる。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 作り直しの spec_graph_pj_changes の行スキーマ契約（7 列。案件 × ノードで 1 行、状態はこの行が持つ）
 * @graph-connects none
 */
export const specPjChangeRowSchema = z
    .object({
    pjNumber: bqInt('案件番号。分割の鍵を兼ねる（AIRCLOSET-139162 なら 139162）'),
    nodeId: bqInt('対象のノードの id。（pjNumber, nodeId）でこの表の 1 行が決まる'),
    before: bqString('書いたときの master の値の JSON（{ nodeType, properties }）。案件が新しく作ったノードなら NULL').nullable(),
    after: bqString('案件を当てたあとの値の JSON（{ nodeType, properties }）。案件が消すノードなら NULL').nullable(),
    status: bqString('この行の今の状態 — open / pending / approved / rejected / merged'),
    updatedAt: bqTimestamp('この行を最後に書いた日時'),
    actor: bqString('この行を最後に書いた主体のメールアドレス'),
})
    .refine((row) => row.before !== null || row.after !== null, {
    message: '案件の変更の行は before と after の両方が NULL にはならない（その行は消す）',
    path: ['after'],
})
    .refine((row) => SPEC_PJ_CHANGE_STATUSES.some((status) => status === row.status), {
    message: 'status は 5 値のいずれかである',
    path: ['status'],
});
/**
 * 操作履歴の `kind` 9 値（HIST §3.2）。**何の id が `targetId` に入るかをこの値が決める。**
 *
 * ノードの連番と要求・why の連番は別なので、番号だけでは対象が決まらない。ノードの履歴を
 * 引くときは、ノードへの操作の 4 値（`upsert` / `delete` / `link` / `revert`）に絞る。
 *
 * | kind | targetId | before / after |
 * |---|---|---|
 * | `upsert` / `delete` / `link` / `revert` | ノードの id | ノードの値（案件を当てた姿） |
 * | `merge` | NULL | NULL |
 * | `approve` / `reject` | 判断した承認対象（`APPROVAL_TARGET_NODE_TYPES`）の id | `reject` の `after` に却下の理由。それ以外は NULL |
 * | `requirement` | 要求の id | `{ statement, acceptance }` |
 * | `why` | why の id | `{ statement, outcome, evidences[] }` |
 *
 * 既存の変更を要求へ結ぶ操作（`link_spec_requirement`）に当たる `kind` は無い。今も変更行を
 * 積まない操作なので、操作履歴にも行を足さない（HIST §3.2）。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 操作履歴の操作の種類 9 値の閉集合。targetId が何の id かをこの値が決める
 * @graph-connects none
 */
export const SPEC_OPERATION_KINDS = [
    'upsert',
    'delete',
    'link',
    'revert',
    'merge',
    'approve',
    'reject',
    'requirement',
    'why',
];
/**
 * ノードへの操作を表す `kind` 4 値。ノードの履歴を引くときの絞り込みに使う（HIST §3.2）。
 *
 * 含める側を列挙する。除外で書くと、`kind` を足したときに黙って「ノードへの操作」に
 * 数えられ、要求や why の id をノードの id として引きに行く。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business targetId がノードの id である kind 4 値の allowlist
 * @graph-connects none
 */
export const SPEC_NODE_OPERATION_KINDS = [
    'upsert',
    'delete',
    'link',
    'revert',
];
/**
 * 操作履歴 `spec_graph_operations` の行（9 列。HIST §3.2）。**足すだけで、消さず書き換えない。**
 *
 * 案件の変更の行は書き直しのたびに置き換わって最後の書き手しか残らない。途中の書き手と
 * 判断した人を辿れるのはこの表だけになるので、`actor` と `at` は全種類の操作で必須である
 * （書込も、承認も、確定も、打ち消しも）。`actor` は引数では受けず、認証済みの主体から埋める。
 *
 * **状態を持たない。** 確定済みか・承認済みかは案件の変更の行の `status` が答える
 * （`specPjChangeRowSchema`）。ここに足す確定・承認・却下の行は記録であって、状態の判定には
 * 1 度も使わない。
 *
 * `refOpId` を持つのは打ち消し（`revert`）だけで、打ち消した操作の `opId` を指す。
 *
 * 分割は `pjNumber`、クラスタは `targetId` / `kind` / `at`（今の変更ログと同じ考え方）。
 * 分割の有効期限は置かない — 打ち消しが読む唯一の材料だからである。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 作り直しの spec_graph_operations の行スキーマ契約（9 列。足すだけの操作履歴で、状態を持たない）
 * @graph-connects none
 */
export const specOperationRowSchema = z
    .object({
    opId: bqInt('操作の id。表ごとの連番'),
    at: bqTimestamp('操作した日時'),
    actor: bqString('操作した主体のメールアドレス。全種類の操作で必須'),
    pjNumber: bqInt('どの案件の中での操作か。分割の鍵を兼ねる'),
    kind: bqString('操作の種類 — upsert / delete / link / revert / merge / approve / reject / requirement / why'),
    targetId: bqInt('操作の対象の id。何の id かは kind が決める。merge では NULL').nullable(),
    before: bqString('操作の直前の値の JSON。無ければ NULL').nullable(),
    after: bqString('操作の直後の値の JSON。無ければ NULL').nullable(),
    refOpId: bqInt('打ち消した操作の opId。revert 以外は NULL').nullable(),
})
    .refine((row) => SPEC_OPERATION_KINDS.some((kind) => kind === row.kind), {
    message: 'kind は 9 値のいずれかである',
    path: ['kind'],
})
    .refine((row) => row.kind === 'revert' || row.refOpId === null, {
    message: 'refOpId を持てるのは revert だけである',
    path: ['refOpId'],
});
/**
 * 要求と案件の変更の結び `spec_graph_requirement_changes` の行（3 列。HIST §3.3）。
 *
 * **結ぶ相手が変更行から（案件, ノード）へ替わる。** 旧い形の表（T1a で消した）は（要求, 変更行の id）の 2 列で、
 * 書込 1 回ごとに積まれた行を指している。作り直しでは変更行そのものが無くなり、案件の変更は
 * （案件, ノード）で 1 行になるので、結びもその鍵を指す。同じ案件が同じノードに 16 回書いても
 * 結びは 1 本のままになる。
 *
 * 要求と why の**本文の列は替わらない**（`statement` / `acceptance` / `outcome` / 根拠）。
 * 替わるのは id が number になることだけなので、本文側の行の形はここで宣言し直さない。形ごと
 * 替わるのはこの結びの表だけである。
 *
 * `pjNumber` を持つのは、案件の一覧が「その案件が持つ要求の件数」を 1 表の等値比較で数える
 * ためである。ノードの id だけでは、その行がどの案件のものかを引き直さないと分からない。
 *
 * **今の 2 列（`requirementId` / `changeId`。どちらも STRING）と同じ物理表を指す。** infra の
 * 表定義はこの段では動かさない。新しい書込経路はどこからも配線していないので実害は
 * 無いが、**切替では「物理表のスキーマの入れ替え」と「旧経路の廃止」を同じ 1 回で行う**。
 * 片方だけを配線すると、同じ表へ列の合わない書込が 2 経路から入る。
 *
 * 分割は `pjNumber`、クラスタは `requirementId` と `nodeId`。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 作り直しの spec_graph_requirement_changes の行スキーマ契約（3 列。要求と（案件, ノード）を結ぶ）
 * @graph-connects none
 */
export const specElementRequirementLinkRowSchema = z.object({
    requirementId: bqInt('要求の id'),
    pjNumber: bqInt('案件番号。分割の鍵を兼ねる'),
    nodeId: bqInt('その要求を実現する案件の変更の行のノードの id'),
});
/**
 * 要求 `spec_graph_requirements` の行（5 列）。**何を実現するか**を持つ。
 *
 * 今の要求の表から替わるのは
 * `requirementId` の型だけである（UUID の文字列 → 表ごとの連番の INT64）。本文の 2 項目
 * （`statement` / `acceptance`）と、立てた時刻・立てた人はそのまま持つ — 要求と why は仕様の
 * 形に依存しないので、作り直しで中身が動かないためである（要求・why 設計 §1）。
 *
 * **受入条件は自然文のまま持つ。** 判定表への参照にする案は採らなかった。要求は AI には
 * 書けないので、機械で判定できる形にしても書き手が居ない（要求・why 設計 §2）。
 *
 * `raisedAt` と `actor` を残すのは、この行が「いつ誰が立てたか」の唯一の置き場だからである。
 * 直した記録（`update_spec_requirement`）は操作履歴の `requirement` の行が持ち、この 2 列は
 * 直しても動かさない。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 作り直しの spec_graph_requirements の行スキーマ契約（5 列。id は INT64 の正の整数）
 * @graph-connects none
 */
export const specElementRequirementRowSchema = z
    .object({
    requirementId: bqInt('要求の id。表ごとの連番'),
    statement: bqString('実現することを 1 文で。理由は書かない（それは why が持つ）。例: 選定引き当てにマッチ度の概念を入れる'),
    acceptance: bqString('実現したと言える条件。自然文で人が判定する。紐づく変更が全部入っても要求を満たすとは限らない'),
    raisedAt: bqTimestamp('この要求を立てた時刻'),
    actor: bqString('この要求を立てた主体のメールアドレス'),
})
    .refine((row) => row.requirementId > 0, {
    message: '行の requirementId は正の整数である（0 と負の数は採番前・仮の番号のしるし）',
    path: ['requirementId'],
});
/**
 * why `spec_graph_whys` の行（5 列）。**その要求がなぜ必要か**を持つ。
 *
 * 今の `specGraphWhyRowSchema` から替わるのは `whyId` の型だけである。`statement` に解決策を
 * 書かせず、`outcome` に測り方まで書かせる縛りもそのまま持つ — 無いと why が永久に開いた
 * ままになって棚卸しが回らないためである。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 作り直しの spec_graph_whys の行スキーマ契約（5 列。id は INT64 の正の整数）
 * @graph-connects none
 */
export const specElementWhyRowSchema = z
    .object({
    whyId: bqInt('why の id。表ごとの連番'),
    statement: bqString('困りごとを 1 文で。誰が・どの場面で・何に困っているか。解決策は書かない（それは要求が持つ）'),
    outcome: bqString('変えたい状態と、その測り方'),
    raisedAt: bqTimestamp('この why を立てた時刻'),
    actor: bqString('この why を立てた主体のメールアドレス'),
})
    .refine((row) => row.whyId > 0, {
    message: '行の whyId は正の整数である（0 と負の数は採番前・仮の番号のしるし）',
    path: ['whyId'],
});
/**
 * 根拠 `spec_graph_why_evidences` の行（5 列）。why 1 件が 1 件以上持つ**観測**。
 *
 * 今の `specGraphWhyEvidenceRowSchema` から替わるのは 2 つの id の型だけである。`observedAt`
 * を TIMESTAMP 列のまま持つのは、陳腐化を機械で判定できる欄がここしか無いためである（why の
 * 入れ子の列に畳むと型が落ちる。契約から BQ スキーマを導く `zodToBqSchema` はスカラ 5 型しか
 * 扱わない）。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 作り直しの spec_graph_why_evidences の行スキーマ契約（5 列。2 つの id は INT64 の正の整数）
 * @graph-connects none
 */
export const specElementWhyEvidenceRowSchema = z
    .object({
    whyId: bqInt('この根拠が属する why の id'),
    evidenceId: bqInt('根拠の id。表ごとの連番'),
    source: bqString('原典の URL か文書 ID（Backlog / Grafana / 議事録 / 法令条番号）。第三者が原典に当たれること'),
    observedAt: bqTimestamp('その観測をした日時。書込日時ではない。陳腐化を機械で判定できる唯一の欄'),
    value: bqString('観測値そのもの。要約ではなく値。再測定のベースラインになる'),
})
    .refine((row) => row.whyId > 0 && row.evidenceId > 0, {
    message: '行の whyId と evidenceId は正の整数である（0 と負の数は採番前・仮の番号のしるし）',
    path: ['evidenceId'],
});
/**
 * 案件と要求の結び `spec_graph_pj_requirements` の行（2 列）。案件 : 要求 = N:M。
 *
 * 今の `specGraphPjRequirementRowSchema` から替わるのは 2 列とも型である（`pjNumber` は
 * 案件番号が number になるため、`requirementId` は表ごとの連番になるため）。
 *
 * 列は 2 つだけである。誰がいつ結んだかは操作履歴が持つので、ここに `linkedAt` / `actor` を
 * 置くと同じ事実の 2 表現になる。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 作り直しの spec_graph_pj_requirements の行スキーマ契約（2 列。案件と要求の N:M 結び）
 * @graph-connects none
 */
export const specElementPjRequirementRowSchema = z
    .object({
    pjNumber: bqInt('案件番号（AIRCLOSET-139162 なら 139162）'),
    requirementId: bqInt('その案件が持つ要求の id'),
})
    .refine((row) => row.pjNumber > 0 && row.requirementId > 0, {
    message: '行の pjNumber と requirementId は正の整数である',
    path: ['requirementId'],
});
/**
 * 要求と why の結び `spec_graph_requirement_whys` の行（2 列）。要求 : why = N:M。
 *
 * 今の `specGraphRequirementWhyRowSchema` から替わるのは 2 列とも id の型である。1 つの要求が
 * 複数の困りごとを解き、1 つの困りごとが複数の要求を生むので N:M のまま持つ。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 作り直しの spec_graph_requirement_whys の行スキーマ契約（2 列。要求と why の N:M 結び）
 * @graph-connects none
 */
export const specElementRequirementWhyRowSchema = z
    .object({
    requirementId: bqInt('要求の id'),
    whyId: bqInt('その要求がなぜ必要かを説明する why の id'),
})
    .refine((row) => row.requirementId > 0 && row.whyId > 0, {
    message: '行の requirementId と whyId は正の整数である',
    path: ['whyId'],
});
/**
 * 要求の層 6 表のクラスタリング列。**表定義（infra）と読取の述語がこの宣言を共有する。**
 *
 * 今の要求の層のクラスタリングから替わるのは
 * 結びの表 1 つだけで、`changeId` が `nodeId` になる。結ぶ相手が変更行から（案件, ノード）へ
 * 替わり、`changeId` の列そのものが無くなるためである。残る 5 表は列の名前が動かないので
 * 並びも動かさない。
 *
 * **クラスタは飾りではなく、この設計が成立する条件である。** 主経路が id の集合による引き
 * （`WHERE whyId IN UNNEST(@ids)`）で、分割では刈れない形だからである（飛び飛びの集合は
 * 範囲比較に書き換えられない）。一方クラスタは先頭列への素の `IN UNNEST` 述語でブロックを
 * 刈る。読む側が述語を書き忘れてもエラーにはならず全件スキャンになるだけなので、述語に使う
 * 列名を infra 側と読取側で別々に書かず、ここから引く。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 作り直しの要求の層 6 表のクラスタリング列の単一宣言。infra の表定義と読取の述語が共有する
 * @graph-connects none
 */
export const SPEC_ELEMENT_REQUIREMENT_CLUSTERINGS = {
    requirements: ['requirementId'],
    whys: ['whyId'],
    whyEvidences: ['whyId', 'evidenceId'],
    pjRequirements: ['pjNumber', 'requirementId'],
    requirementWhys: ['requirementId', 'whyId'],
    requirementChanges: ['requirementId', 'nodeId'],
};
//# sourceMappingURL=rows.js.map